import { DisposableStore } from "@codesandbox/pitcher-common";
import type {
  CustomViewOption,
  ViewContainer,
} from "@codingame/monaco-vscode-workbench-service-override";
import { useEnvironmentInterface } from "environment-interface";
import * as React from "react";
import { createPortal } from "react-dom";
import { IViewDescriptorService, StandaloneServices } from "vscode/services";

import { useVSCodeExtensionInitializedEffect } from "hooks/useVSCodeExtensionInitializedEffect";

interface CustomSidebarViewProps {
  viewOptions: Omit<CustomViewOption, "renderBody">;
  viewContainerId?: string;
  children: React.ReactNode;
}

export const CustomSidebarView: React.FC<CustomSidebarViewProps> = ({
  viewOptions,
  viewContainerId,
  children,
}) => {
  const [element, setElement] = React.useState<HTMLElement | null>(null);
  const { monacoEditor } = useEnvironmentInterface();

  useVSCodeExtensionInitializedEffect(() => {
    const renderBody: CustomViewOption["renderBody"] = (element) => {
      setElement(element);

      return {
        dispose() {
          setElement(null);
        },
      };
    };

    let viewContainer: ViewContainer | undefined = undefined;
    if (viewContainerId) {
      viewContainer =
        StandaloneServices.get(IViewDescriptorService).getViewContainerById(
          viewContainerId,
        ) ?? undefined;
    }

    let isDisposed = false;
    const disposableStore = new DisposableStore();
    disposableStore.add({
      dispose: () => {
        isDisposed = true;
      },
    });
    monacoEditor.getRegisterCustomView().then((registerCustomView) => {
      if (isDisposed) {
        return;
      }

      disposableStore.add(
        registerCustomView({
          ...viewOptions,
          viewContainer,
          renderBody,
        }),
      );
    });

    return () => disposableStore.dispose();

    // Don't add viewOptions here! It will cause the view to get readded and removed every render.
    // `viewOptions` always results in a new object, so it will always be a new reference.
  }, []);

  if (!element) return null;

  return createPortal(children, element);
};
